import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  signOut as firebaseSignOut,
  User as FirebaseUser
} from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { logAuth } from '../lib/audit';
import { refreshPushToken, detachPushOnLogout, listenForegroundPush } from '../lib/push';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { AuthContextType } from '../types/auth';
import { User } from '../types/user';
import { userService } from '../services/userService';
import toast from 'react-hot-toast';
import { useLanguage } from './LanguageContext';

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<(FirebaseUser & User) | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  // Update the lastLoginAt field for the user
  const updateLastLogin = async (userId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        lastLoginAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating last login:', error);
    }
  };

  // Napló: belépés (a cég azonosítóval, hogy az admin a saját cégénél lássa)
  const logLogin = async (userId: string, method: string) => {
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      await logAuth('login', { organizationId: (snap.data() as any)?.organizationId || null, method });
    } catch (e) { console.warn('login audit failed', e); }
  };

  // Push token az aktuális felhasználóhoz (ha ezen az eszközön be van kapcsolva)
  useEffect(() => {
    if (!user?.id) return;
    refreshPushToken((user as any)?.organizationId || null);
    listenForegroundPush();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getRedirectResult(auth).catch((error) => {
      console.error('[AuthContext] Redirect result error:', error);
    });

    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      try {
        if (firebaseUser) {
          let userData: User;
          try {
            userData = await userService.createUserIfNotExists(firebaseUser);
          } catch (dbError: any) {
            // Nincs adatlap és meghívó sincs → nem léphet be (regisztráció kivezetve)
            if (dbError?.code === 'no-account' || dbError?.code === 'functions/permission-denied') {
              await firebaseSignOut(auth);
              toast.error(dbError?.message || 'Ehhez a fiókhoz nincs hozzáférés. Kérd az adminisztrátor meghívását.', { duration: 8000 });
              setUser(null);
              setLoading(false);
              return;
            }
            console.error('[AuthContext] Firestore hiba, fallback to basic user:', dbError);
            userData = {
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || '',
              photoURL: firebaseUser.photoURL || '',
              role: 'user',
              organizationId: '',
              onboardingCompleted: false,
              createdAt: new Date(),
              updatedAt: new Date(),
              lastLoginAt: new Date()
            };
          }
          
          // Check if user is active (for customer role)
          if (userData.role === 'customer' && (userData as any).isActive === false) {
            await firebaseSignOut(auth);
            toast.error(t('common.messages.authContext.accountDeactivated'));
            setUser(null);
            setLoading(false);
            return;
          }
          
          // Update the lastLoginAt timestamp (non-blocking)
          updateLastLogin(firebaseUser.uid).catch((e) => 
            console.error('[AuthContext] lastLogin update failed:', e)
          );
          
          // Include the lastLoginAt in the user data
          const updatedUserData = {
            ...userData,
            lastLoginAt: new Date()
          };
          
          const finalUser = { ...firebaseUser, ...updatedUserData };
          
          setUser(finalUser);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('[AuthContext] Hiba a felhasználó beállítása során:', error);
        toast.error(t('common.messages.authContext.error'));
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    // Check if running in Electron
    if (window.electron?.isElectron) {
      // Electron-specific OAuth flow
      try {
        await window.electron.googleOAuth();
        // Note: Firebase Auth popup doesn't work well in Electron
        // For now, show message to use email/password login
        throw new Error('Google login is not fully supported in desktop app. Please use email/password login.');
      } catch (error) {
        console.error('Electron OAuth error:', error);
        throw error;
      }
    }

    // Web-based OAuth flow
    const provider = new GoogleAuthProvider();
    
    // Google Calendar hozzáférés kérése
    // KIKOMMENTEZVE: Könnyebb bejelentkezés érdekében
    // provider.addScope('https://www.googleapis.com/auth/calendar');
    // provider.addScope('https://www.googleapis.com/auth/calendar.events');
    
    // Mindig kérje el a hozzáférést (ne használja a cached tokent)
    // KIKOMMENTEZVE: Könnyebb bejelentkezés érdekében
    // provider.setCustomParameters({
    //   prompt: 'consent',
    //   access_type: 'offline' // Refresh token kérése
    // });
    
    try {
      const result = await signInWithPopup(auth, provider);
      
      // Mentjük a Google OAuth tokent a Firestore-ba
      // KIKOMMENTEZVE: Könnyebb bejelentkezés érdekében
      // if (result.user) {
      //   const credential = GoogleAuthProvider.credentialFromResult(result);
      //   
      //   if (credential) {
      //     const accessToken = credential.accessToken;
      //     // A refresh token sajnos nem érhető el a Firebase Auth-ban közvetlenül
      //     // Ezért használjuk az access tokent és frissítjük amikor lejár
      //     
      //     try {
      //       await setDoc(doc(db, 'userTokens', result.user.uid), {
      //         googleAccessToken: accessToken,
      //         updatedAt: new Date(),
      //         provider: 'google.com'
      //       }, { merge: true });
      //       
      //       console.log('Google OAuth token saved successfully');
      //     } catch (tokenError) {
      //       console.error('Error saving OAuth token:', tokenError);
      //     }
      //   }
      //   
      //   await updateLastLogin(result.user.uid);
      // }
      
      if (result.user) {
        await updateLastLogin(result.user.uid);
        logLogin(result.user.uid, 'google');
      }
      
      toast.success(t('common.messages.authContext.signInSuccess'));
    } catch (error: any) {
      // MFA required - dobja tovább a hibát
      if (error.code === 'auth/multi-factor-auth-required') {
        throw error; // Ne kezeljük itt, hanem dobja tovább
      }
      
      console.error('Error signing in with Google:', error);
      toast.error(t('common.messages.authContext.signInError'));
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Update last login time when user signs in
      if (result.user) {
        await updateLastLogin(result.user.uid);
        logLogin(result.user.uid, 'email');
      }
      
      toast.success(t('common.messages.authContext.signInSuccess'));
      return { success: true };
    } catch (error: any) {
      // MFA required - dobja tovább a hibát, hogy a Login.tsx kezelje
      if (error.code === 'auth/multi-factor-auth-required') {
        throw error; // Ne kezeljük itt, hanem dobja tovább
      }
      
      console.error('Error signing in with email:', error);
      if (['auth/wrong-password', 'auth/invalid-credential', 'auth/user-not-found', 'auth/too-many-requests', 'auth/user-disabled'].includes(error.code)) {
        logAuth('login_failed', { email, method: `email · ${error.code.replace('auth/', '')}` });
      }
      
      // Handle specific Firebase Auth errors
      let errorMessage = t('common.messages.authContext.signInError');
      
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          errorMessage = 'Hibás e-mail cím vagy jelszó.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Érvénytelen e-mail cím formátum.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Túl sok sikertelen próbálkozás. Próbáld újra később.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Ez a felhasználói fiók le van tiltva.';
          break;
        default:
          errorMessage = 'Hibás e-mail cím vagy jelszó.';
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name if provided
      if (displayName && result.user) {
        await updateProfile(result.user, { displayName });
      }
      
      // Send email verification
      if (result.user) {
        try {
          await sendEmailVerification(result.user);
          toast.success('Regisztráció sikeres! Ellenőrizd az email fiókodat a megerősítő linkért.', {
            duration: 6000,
            icon: '📧'
          });
        } catch (verifyError) {
          console.error('Error sending verification email:', verifyError);
          toast('Regisztráció sikeres, de a megerősítő email küldése sikertelen. Kérj új linket a profilodban.', {
            duration: 6000,
            icon: '⚠️'
          });
        }
      }
      
      // Update last login time when user signs up
      if (result.user) {
        await updateLastLogin(result.user.uid);
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error signing up with email:', error);
      
      // Handle specific Firebase Auth errors
      let errorMessage = 'Regisztrációs hiba történt.';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Ez az e-mail cím már használatban van.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Érvénytelen e-mail cím formátum.';
          break;
        case 'auth/weak-password':
          errorMessage = 'A jelszó túl gyenge. Legalább 6 karakter szükséges.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'E-mail regisztráció nincs engedélyezve.';
          break;
        default:
          errorMessage = 'Regisztrációs hiba történt.';
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Jelszó visszaállító e-mail elküldve! Ellenőrizd a postafiókod.');
      return { success: true };
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      
      let errorMessage = 'Hiba a jelszó visszaállító e-mail küldése során.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Nem található felhasználó ezzel az e-mail címmel.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Érvénytelen e-mail cím formátum.';
          break;
        default:
          errorMessage = 'Hiba a jelszó visszaállító e-mail küldése során.';
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signOut = async () => {
    try {
      await logAuth('logout', { organizationId: (user as any)?.organizationId || null });
      await detachPushOnLogout();
      await firebaseSignOut(auth);
      toast.success(t('common.messages.authContext.signOutSuccess'));
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error(t('common.messages.authContext.signOutError'));
    }
  };

  const refreshUser = async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) return;
    try {
      const userData = await userService.createUserIfNotExists(fbUser);
      setUser({ ...fbUser, ...userData, lastLoginAt: new Date() } as any);
    } catch (e) {
      console.error('[AuthContext] refreshUser failed:', e);
    }
  };

  const setActiveOrganization = async (organizationId: string) => {
    if (!user?.id) return;
    await userService.updateUser(user.id, { organizationId } as any);
    setUser(prev => (prev ? ({ ...prev, organizationId } as any) : prev));
  };

  const value: AuthContextType = {
    user,
    currentUser: user,
    loading,
    refreshUser,
    setActiveOrganization,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 